import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';
import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme, getMetricColor} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig} from '../types';

/**
 * Irradiance and accumulated energy are absolute SI readings — they don't
 * follow the user's unit system, so both labels are fixed.
 */
const SOLAR_RADIATION_UNIT = 'W/m\u00B2';
const SOLAR_ENERGY_UNIT = 'kJ/m\u00B2';

export interface SolarChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  comparisonData?: ChartDataPoint[];
  comparisonLabel?: string;
  dateTimeConfig?: DateTimeConfig;
  /**
   * Names the series in the user's language. Required: the names are
   * messages, and English would otherwise reach every locale.
   */
  formatMessage: (descriptor: MessageDescriptor) => string;
}

export const solarChart = (options: SolarChartOptions): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    height = 250,
    comparisonData,
    comparisonLabel,
    dateTimeConfig,
    formatMessage,
  } = options;

  const series: SeriesConfig[] = [
    {
      id: 'solar-radiation',
      type: comparisonData ? 'line' : 'area',
      dataKey: 'SOLAR_RADIATION',
      name: formatMessage(messages.metrics.solarRadiation),
      color: getMetricColor('solar', theme.isDark),
      yAxisIndex: 0,
      interpolation: 'monotone',
      fillOpacity: 0.3,
      lineWidth: 2,
      showMarkers: false,
      zIndex: 1,
    },
  ];

  if (comparisonData) {
    series.push({
      id: 'solar-comparison',
      type: 'line',
      dataKey: 'SOLAR_RADIATION_COMP',
      name: comparisonLabel ?? formatMessage(messages.charts.comparison),
      color: theme.colors.textSecondary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      dashArray: '5 3',
      showMarkers: false,
      zIndex: 0,
    });
  }

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({type: 'datetime', timezone, granularity, theme}),
    yAxes: [
      createYAxisConfig({
        id: 'solar',
        metric: 'SOLAR_RADIATION',
        units,
        theme,
        domain: [0, 'auto'],
        unitLabel: SOLAR_RADIATION_UNIT,
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'SOLAR_RADIATION',
      units,
      theme,
      dateTimeConfig,
    }),
    legend: createLegendConfig(series.length > 1, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};

export interface SolarReportChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  /** 'D' for the monthly report (one point per day), 'M' for the annual one. */
  granularity: DataGranularity;
  /** X-axis labels: day numbers for a month, short month names for a year. */
  categories: string[];
  /** Tooltip headers, indexed by the point's `timestamp` (its slot index). */
  tooltipLabels?: string[];
  height?: number;
  /** Translated legend/tooltip label for the radiation line. */
  radiationLabel: string;
  /** Translated legend/tooltip label for the energy bars. */
  energyLabel: string;
}

/**
 * Combo chart for the monthly and annual solar reports: average radiation as a
 * line on the left axis, accumulated energy as bars on the right one.
 *
 * The two series carry different units, so each gets its own tooltip formatter
 * — the shared `metric` formatter would label the energy bars in W/m².
 */
export const solarReportChart = (
  options: SolarReportChartOptions,
): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    categories,
    tooltipLabels,
    height = 250,
    radiationLabel,
    energyLabel,
  } = options;

  const series: SeriesConfig[] = [
    {
      id: 'solar-radiation',
      type: 'line',
      dataKey: 'SOLAR_RADIATION',
      name: radiationLabel,
      color: getMetricColor('solar', theme.isDark),
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      showMarkers: false,
      zIndex: 2,
    },
    {
      id: 'solar-energy',
      type: 'bar',
      dataKey: 'SOLAR_ENERGY',
      name: energyLabel,
      color: theme.colors.secondary,
      yAxisIndex: 1,
      interpolation: 'linear',
      showMarkers: false,
      barRadius: [theme.borderRadius, theme.borderRadius, 0, 0],
      zIndex: 1,
    },
  ];

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({type: 'category', timezone, theme, categories}),
    yAxes: [
      createYAxisConfig({
        id: 'solar',
        theme,
        unitLabel: SOLAR_RADIATION_UNIT,
      }),
      createYAxisConfig({
        id: 'energy',
        theme,
        unitLabel: SOLAR_ENERGY_UNIT,
        position: 'right',
        gridLines: false,
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'SOLAR_RADIATION',
      units,
      theme,
      tooltipLabels,
      customFormatters: {
        'solar-radiation': (value: number) =>
          `${value.toFixed(0)} ${SOLAR_RADIATION_UNIT}`,
        'solar-energy': (value: number) =>
          `${value.toFixed(0)} ${SOLAR_ENERGY_UNIT}`,
      },
    }),
    legend: createLegendConfig(true, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
